// "Çevrem" — the user's social circle: friends and the groups they belong to.
// Friends/groups power the matching pool (who you can invite), so this is where
// you see and make sense of that network, and where you manage it: accept friend
// requests, add/remove friends, and create/leave groups.

import $ from 'jquery';
import type { FriendRequestView, GroupSummary } from '@shared/dto';
import type { User } from '@shared/models';
import type { Screen } from './types';
import { api } from '../api';
import { onCleanup, refresh } from '../router';
import { getCurrentUser, refreshUnread } from '../session';
import { avatarHtml, esc } from '../ui/dom';
import { SKILL_TR, skillClass } from '../ui/format';
import { emptyHtml, errorHtml, loadingHtml } from '../ui/states';
import { toast } from '../ui/toast';
import { confirmDialog } from '../ui/confirm';

function requestRowHtml(r: FriendRequestView): string {
  return `<div class="roster__row">
    ${avatarHtml(r.from.name, r.from.avatarUrl, 36)}
    <span class="roster__name">${esc(r.from.name)}</span>
    <span class="roster__area">${esc(r.from.homeArea)}</span>
    <button class="btn btn--primary btn--sm" data-accept="${esc(r.requestId)}">Kabul</button>
    <button class="btn btn--danger btn--sm" data-decline="${esc(r.requestId)}">Reddet</button>
  </div>`;
}

function friendRowHtml(u: User): string {
  return `<div class="roster__row">
    ${avatarHtml(u.name, u.avatarUrl, 36)}
    <span class="roster__name">${esc(u.name)}</span>
    <span class="roster__area">${esc(u.homeArea)}</span>
    <span class="badge ${skillClass(u.skill)}">${SKILL_TR[u.skill]}</span>
    <button class="btn btn--ghost btn--sm icon-btn" data-remove="${esc(u.id)}" aria-label="Arkadaşlıktan çıkar" title="Çıkar">✕</button>
  </div>`;
}

function groupCardHtml(g: GroupSummary, meId: string): string {
  const isOwner = g.ownerId === meId;
  const members = g.members
    .map(
      (m) => `<span class="grp-member">${avatarHtml(m.name, m.avatarUrl, 24)}${esc(m.name.split(' ')[0])}</span>`,
    )
    .join('');
  const action = isOwner
    ? `<a class="btn btn--ghost btn--sm" href="#/group/${esc(g.id)}">Düzenle</a>`
    : `<button class="btn btn--ghost btn--sm" data-leave="${esc(g.id)}">Ayrıl</button>`;
  return `<div class="card grp-card">
    <div class="grp-card__head">
      <span class="grp-card__name">${esc(g.name)}</span>
      <span class="badge">${g.members.length} üye</span>
    </div>
    <div class="grp-card__members">${members}</div>
    <div class="grp-card__foot">${action}</div>
  </div>`;
}

export const FriendsScreen: Screen = async (body) => {
  body.html(loadingHtml());
  const meId = getCurrentUser()?.id ?? '';

  let requests: FriendRequestView[];
  let friends: User[];
  let groups: GroupSummary[];
  try {
    [requests, friends, groups] = await Promise.all([
      api.call('friends.requests', undefined),
      api.call('friends.mine', undefined),
      api.call('groups.mine', undefined),
    ]);
  } catch (err) {
    body.html(errorHtml(err instanceof Error ? err.message : String(err)));
    return;
  }

  const requestsSection =
    requests.length > 0
      ? `<div class="section-title">Gelen istekler (${requests.length})</div>
         <div class="roster">${requests.map(requestRowHtml).join('')}</div>`
      : '';

  const friendsSection =
    friends.length > 0
      ? `<div class="roster">${friends.map(friendRowHtml).join('')}</div>`
      : emptyHtml('👋', 'Henüz arkadaşın yok. "Arkadaş ekle" ile başlayabilirsin.');

  const groupsSection =
    groups.length > 0
      ? groups.map((g) => groupCardHtml(g, meId)).join('')
      : emptyHtml('👥', 'Henüz bir grupta değilsin.');

  body.html(`
    <div class="friends">
      ${requestsSection}
      <div class="list-head">
        <div class="section-title">Arkadaşlarım (${friends.length})</div>
        <a class="btn btn--ghost btn--sm" href="#/add-friends">+ Arkadaş ekle</a>
      </div>
      ${friendsSection}
      <div class="list-head">
        <div class="section-title">Gruplarım (${groups.length})</div>
        <a class="btn btn--ghost btn--sm" href="#/group/new">+ Grup oluştur</a>
      </div>
      ${groupsSection}
    </div>
  `);

  body.on('click', '[data-accept]', async function () {
    const requestId = $(this).data('accept') as string;
    try {
      await api.call('friends.respond', { requestId, accept: true });
      toast('Arkadaşlık isteği kabul edildi 🤝', 'success');
      void refreshUnread();
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  body.on('click', '[data-decline]', async function () {
    const requestId = $(this).data('decline') as string;
    await api.call('friends.respond', { requestId, accept: false });
    toast('İstek reddedildi', 'info');
    void refreshUnread();
    refresh();
  });

  body.on('click', '[data-remove]', async function () {
    const userId = $(this).data('remove') as string;
    const friend = friends.find((f) => f.id === userId);
    const ok = await confirmDialog({
      title: 'Arkadaşlıktan çıkar',
      message: friend
        ? `${friend.name} arkadaş listenden çıkarılacak. Bu işlem iki taraf için de geçerli.`
        : 'Bu kişi arkadaş listenden çıkarılacak. Bu işlem iki taraf için de geçerli.',
      confirmLabel: 'Çıkar',
    });
    if (!ok) return;
    await api.call('friends.remove', { userId });
    toast('Arkadaşlıktan çıkarıldı', 'info');
    refresh();
  });

  body.on('click', '[data-leave]', async function () {
    const groupId = $(this).data('leave') as string;
    const group = groups.find((g) => g.id === groupId);
    const ok = await confirmDialog({
      title: 'Gruptan ayrıl',
      message: group
        ? `"${group.name}" grubundan ayrılmak istediğine emin misin?`
        : 'Bu gruptan ayrılmak istediğine emin misin?',
      confirmLabel: 'Ayrıl',
    });
    if (!ok) return;
    try {
      await api.call('groups.leave', { groupId });
      toast('Gruptan ayrıldın', 'info');
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  // Keep the list fresh if a friend request arrives while we're looking at it.
  const off = api.on('notification', (ev) => {
    if (ev.notification.topic.startsWith('friend')) refresh();
  });
  onCleanup(off);
};
